import { Controller } from 'react-hook-form';
import type { Control, FieldErrors } from 'react-hook-form';
import {
  TextField,
  FormControl,
  Autocomplete,
  Box,
  InputAdornment,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Celebration,
  LocalDining,
  Notes,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import {
  OCCASIONS_OPTIONS,
  DIETARY_OPTIONS,
} from '@/constants/reservationEnums';
import type { CustomerInfoFormData } from '@/schemas/customerInfo';

interface ContactFormProps {
  control: Control<CustomerInfoFormData>;
  errors: FieldErrors<CustomerInfoFormData>;
}

export function ContactForm({ control, errors }: ContactFormProps) {
  const { t } = useTranslation();

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {/* First Name */}
      <Controller
        name="firstName"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={`${t('contactForm.firstName')} *`}
            error={!!errors.firstName}
            helperText={errors.firstName?.message}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person />
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      {/* Last Name */}
      <Controller
        name="lastName"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={`${t('contactForm.lastName')} *`}
            error={!!errors.lastName}
            helperText={errors.lastName?.message}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person />
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      {/* Email */}
      <Controller
        name="email"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={`${t('contactForm.email')} *`}
            type="email"
            error={!!errors.email}
            helperText={errors.email?.message}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email />
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      {/* Phone Number */}
      <Controller
        name="phoneNumber"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth error={!!errors.phoneNumber}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Phone sx={{ color: 'action.active', mt: 0.5 }} />
              <Box sx={{ flex: 1 }}>
                <PhoneInput
                  {...field}
                  defaultCountry="GR"
                  placeholder={`${t('contactForm.phoneNumber')} *`}
                  international
                  style={{
                    padding: '16.5px 14px',
                    border: errors.phoneNumber ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '4px',
                    width: '100%',
                  }}
                />
              </Box>
            </Box>
            {errors.phoneNumber && (
              <Box component="span" sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 1.75 }}>
                {errors.phoneNumber.message}
              </Box>
            )}
          </FormControl>
        )}
      />

      {/* Special Occasion */}
      <Controller
        name="specialOccasion"
        control={control}
        render={({ field: { onChange, value, ...field } }) => (
          <Autocomplete
            {...field}
            options={OCCASIONS_OPTIONS}
            getOptionLabel={(option) => t(option.labelKey)}
            value={OCCASIONS_OPTIONS.find((opt) => opt.value === value) || null}
            onChange={(_, data) => onChange(data?.value || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('contactForm.specialOccasion')}
                error={!!errors.specialOccasion}
                helperText={errors.specialOccasion?.message}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <Celebration />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}
      />

      {/* Dietary Restrictions */}
      <Controller
        name="dietaryRestrictions"
        control={control}
        defaultValue={[]}
        render={({ field: { onChange, value, ...field } }) => (
          <Autocomplete
            {...field}
            multiple
            options={DIETARY_OPTIONS}
            getOptionLabel={(option) => t(option.labelKey)}
            value={DIETARY_OPTIONS.filter((opt) =>
              (value || []).includes(opt.value)
            )}
            onChange={(_, data) => onChange(data.map((item) => item.value))}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('contactForm.dietaryRestrictions')}
                error={!!errors.dietaryRestrictions}
                helperText={errors.dietaryRestrictions?.message}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <LocalDining />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}
      />

      {/* Special Requests / Notes */}
      <Controller
        name="note"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('contactForm.notes')}
            multiline
            rows={3}
            error={!!errors.note}
            helperText={errors.note?.message}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Notes />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}
